import { useEffect, useState } from "react";
import PlatformLayout from "../components/PlatformLayout";
import { getPlatformDashboard } from "@/utils/platformApi";

export default function PlatformDashboardPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await getPlatformDashboard();
      setData(res);
    } catch (err) {
      console.error('获取数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PlatformLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-gray-400" />
        </div>
      </PlatformLayout>
    );
  }

  const stats = data?.stats || {};
  const today = data?.today || {};
  const recentAgents = data?.recent_agents || [];

  return (
    <PlatformLayout>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {/* 代理商总数 */}
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-blue-50 rounded-xl">
              <i className="ri-user-settings-line text-blue-500 text-lg" />
            </div>
            <span className="text-xs text-green-500 bg-green-50 px-2 py-0.5 rounded-full">+{today.agents || 0} 今日</span>
          </div>
          <div className="text-2xl font-bold text-gray-800 mb-1">{stats.agent_count || 0}</div>
          <div className="text-sm text-gray-500">代理商总数</div>
        </div>

        {/* 站点总数 */}
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-xl">
              <i className="ri-global-line text-orange-500 text-lg" />
            </div>
            <span className="text-xs text-green-500 bg-green-50 px-2 py-0.5 rounded-full">+{today.sites || 0} 今日</span>
          </div>
          <div className="text-2xl font-bold text-gray-800 mb-1">{stats.site_count || 0}</div>
          <div className="text-sm text-gray-500">站点总数</div>
        </div>

        {/* 用户总数 */}
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-purple-50 rounded-xl">
              <i className="ri-team-line text-purple-500 text-lg" />
            </div>
            <span className="text-xs text-green-500 bg-green-50 px-2 py-0.5 rounded-full">+{today.users || 0} 今日</span>
          </div>
          <div className="text-2xl font-bold text-gray-800 mb-1">{stats.user_count || 0}</div>
          <div className="text-sm text-gray-500">用户总数</div>
        </div>

        {/* 信息总数 */}
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl">
              <i className="ri-file-list-3-line text-green-500 text-lg" />
            </div>
            <span className="text-xs text-green-500 bg-green-50 px-2 py-0.5 rounded-full">+{today.listings || 0} 今日</span>
          </div>
          <div className="text-2xl font-bold text-gray-800 mb-1">{stats.listing_count || 0}</div>
          <div className="text-sm text-gray-500">信息总数</div>
        </div>
      </div>

      {/* 站点状态分布 */}
      {data?.site_stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-green-600">{data.site_stats.active || 0}</div>
            <div className="text-sm text-gray-500">运行中</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-yellow-600">{data.site_stats.stopped || 0}</div>
            <div className="text-sm text-gray-500">已停用</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-red-600">{data.site_stats.expired || 0}</div>
            <div className="text-sm text-gray-500">已过期</div>
          </div>
        </div>
      )}

      {/* 近期代理商 */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">近期代理商</h3>
          <a href="/platform/agents" className="text-sm text-blue-500 hover:text-blue-600">查看全部</a>
        </div>
        <div className="p-5">
          {recentAgents.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    <th className="pb-3 font-medium">代理商</th>
                    <th className="pb-3 font-medium">站点数</th>
                    <th className="pb-3 font-medium">状态</th>
                    <th className="pb-3 font-medium">注册时间</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {recentAgents.map((agent: any) => (
                    <tr key={agent.id} className="border-b border-gray-50">
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-medium">
                            {agent.nickname?.charAt(0) || agent.username?.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium text-gray-800">{agent.nickname || agent.username}</div>
                            <div className="text-xs text-gray-400">@{agent.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-gray-800 font-medium">{agent.site_count}</td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          agent.status === 'active' ? 'bg-green-50 text-green-600' :
                          agent.status === 'suspended' ? 'bg-red-50 text-red-600' :
                          'bg-yellow-50 text-yellow-600'
                        }`}>
                          {agent.status === 'active' ? '正常' : agent.status === 'suspended' ? '已停用' : '试用中'}
                        </span>
                      </td>
                      <td className="py-3 text-gray-500">{agent.created_at?.split(' ')[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <i className="ri-user-search-line text-3xl mb-2" />
              <p>暂无代理商</p>
            </div>
          )}
        </div>
      </div>
    </PlatformLayout>
  );
}
